/**
 * WorkspaceModule — universal renderer for all P1-P5 workspace pages.
 *
 * Usage: <WorkspaceModule slug="brand-reputation"/>
 * Backend contract: GET /api/workspace/modules/{slug} returns:
 *   { title, subtitle, hero?, kpi[], sections[] }
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import {
  Loader2, ThumbsUp, ThumbsDown, Minus, MessageSquare, MessageCircle, TrendingUp,
  TrendingDown, Sparkles, Radar, ScrollText, Megaphone, Target, IndianRupee,
  Zap, Users, Package, Upload, Boxes, Image, Send, Rocket, Link as LinkIcon,
  Smartphone, ArrowRight,
} from "lucide-react";

const ICONS: Record<string, any> = {
  ThumbsUp, ThumbsDown, Minus, MessageSquare, MessageCircle, TrendingUp, TrendingDown,
  Sparkles, Radar, ScrollText, Megaphone, Target, IndianRupee, Zap, Users, Package,
  Upload, Boxes, Image, Send, Rocket, LinkIcon, Smartphone,
};

interface Item { label?: string; value?: any; title?: string; subtitle?: string; meta?: string }
interface Section { type: "bars" | "list" | "chips" | "table" | "feed" | "empty"; title: string; items: Item[] }
interface KPI { label: string; value: any; hint?: string; icon?: string; tone?: string }
interface Payload {
  title: string;
  subtitle?: string;
  hero?: { tone?: string; big?: any; label?: string };
  kpi?: KPI[];
  sections?: Section[];
}

export default function WorkspaceModule({ slug }: { slug: string }) {
  const q = useQuery<Payload>({
    queryKey: ["ws-module", slug],
    queryFn: async () => (await api.get(`/workspace/modules/${slug}`)).data,
    refetchInterval: 60_000,
  });

  const d = q.data;
  return (
    <DashboardLayout title={d?.title || "Module"} subtitle={d?.subtitle} seoNoindex>
      {q.isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--sg-muted)]" data-testid="module-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2"/> Loading module data…
        </div>
      ) : (
        <>
          {d?.hero && <Hero hero={d.hero} title={d.title}/>}
          {d?.kpi && d.kpi.length > 0 && (
            <div className={`grid grid-cols-2 gap-3 mb-6 ${d.kpi.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"}`} data-testid="module-kpi">
              {d.kpi.map((k, i) => <KpiCard key={i} k={k}/>)}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(d?.sections || []).map((s, i) => <SectionCard key={i} s={s}/>)}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function Hero({ hero, title }: { hero: Payload["hero"]; title: string }) {
  const gradient: Record<string, string> = {
    emerald: "from-emerald-700 via-[color:var(--sb-navy,#0A1128)] to-emerald-600",
    rose:    "from-rose-700 via-[color:var(--sb-navy,#0A1128)] to-rose-500",
    amber:   "from-amber-600 via-[color:var(--sb-navy,#0A1128)] to-amber-500",
    fuchsia: "from-fuchsia-800 via-[color:var(--sb-navy,#0A1128)] to-[color:var(--sb-saffron,#F97316)]",
    blue:    "from-blue-800 via-[color:var(--sb-navy,#0A1128)] to-blue-500",
    slate:   "from-slate-800 via-[color:var(--sb-navy,#0A1128)] to-slate-600",
  };
  const g = gradient[hero?.tone || "fuchsia"] || gradient.fuchsia;
  return (
    <div className="rounded-2xl overflow-hidden relative border border-[color:var(--sg-border)] mb-6" data-testid="module-hero">
      <div className={`absolute inset-0 bg-gradient-to-br ${g}`}/>
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 40%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(217,70,239,0.3), transparent 40%)" }}/>
      <div className="relative px-6 py-6 sm:px-8 sm:py-7 text-white">
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-white/70">{title}</div>
        <div className="mt-2 font-serif-display text-4xl sm:text-6xl font-bold tabular-nums leading-none">{hero?.big}</div>
        <div className="mt-2 text-sm text-white/70">{hero?.label}</div>
      </div>
    </div>
  );
}

function KpiCard({ k }: { k: KPI }) {
  const Icon = k.icon && ICONS[k.icon] ? ICONS[k.icon] : Sparkles;
  const tone = k.tone || "blue";
  const t: Record<string, string> = {
    blue:    "text-blue-600 dark:text-blue-300 from-blue-500/15",
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300 from-fuchsia-500/15",
    amber:   "text-amber-600 dark:text-amber-300 from-amber-500/15",
    rose:    "text-rose-600 dark:text-rose-300 from-rose-500/15",
    slate:   "text-slate-600 dark:text-slate-300 from-slate-500/15",
  };
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${t[tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${t[tone].split(" ")[0]} ${t[tone].split(" ")[1]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {k.label}
        </div>
        <div className="mt-1.5 font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums truncate">
          {typeof k.value === "number" ? k.value.toLocaleString?.() ?? k.value : k.value}
        </div>
        {k.hint && <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">{k.hint}</div>}
      </div>
    </div>
  );
}

function SectionCard({ s }: { s: Section }) {
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid={`section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3">{s.title}</div>
      {(!s.items || s.items.length === 0) ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4 text-center">No data yet.</div>
      ) : s.type === "bars" ? (
        <Bars items={s.items}/>
      ) : s.type === "chips" ? (
        <Chips items={s.items}/>
      ) : s.type === "list" || s.type === "feed" ? (
        <ListRows items={s.items}/>
      ) : (
        <EmptyPromo items={s.items}/>
      )}
    </div>
  );
}

function Bars({ items }: { items: Item[] }) {
  const max = Math.max(1, ...items.map((i) => Number(i.value) || 0));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-[color:var(--sg-fg)] truncate">{it.label}</span>
            <span className="text-[color:var(--sg-muted)] tabular-nums">{Number(it.value ?? 0).toLocaleString()}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500" style={{ width: `${(Number(it.value ?? 0) / max) * 100}%`, transition: "width 500ms" }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function Chips({ items }: { items: Item[] }) {
  const max = Math.max(1, ...items.map((i) => Number(i.value) || 0));
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it, i) => {
        const scale = 0.85 + ((Number(it.value) || 0) / max) * 0.7;
        return (
          <span key={i} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--sb-saffron,#F97316)]/40 bg-[color:var(--sb-saffron,#F97316)]/10 px-2.5 py-1 text-[color:var(--sg-fg)] font-semibold"
                style={{ fontSize: `${11 * scale}px` }}>
            {it.label} <span className="text-[10px] opacity-60">·{it.value}</span>
          </span>
        );
      })}
    </div>
  );
}

function ListRows({ items }: { items: Item[] }) {
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-[color:var(--sg-fg)] truncate">{it.title}</div>
            {it.meta && <div className="text-[10px] text-[color:var(--sg-muted)] shrink-0">{it.meta}</div>}
          </div>
          {it.subtitle && <div className="mt-0.5 text-xs text-[color:var(--sg-muted)] leading-relaxed line-clamp-2">{it.subtitle}</div>}
        </div>
      ))}
    </div>
  );
}

function EmptyPromo({ items }: { items: Item[] }) {
  const CTA_LINK: Record<string, string> = {
    "launch the product wizard": "/workspace/catalog/manual",
    "bulk import in 3 steps": "/workspace/catalog/bulk",
    "drop a csv here": "/workspace/catalog/bulk",
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => {
        const key = (it.title || "").toLowerCase();
        const link = CTA_LINK[key];
        const Button: any = link ? "a" : "button";
        const linkProps = link ? { href: link } : {};
        return (
          <div key={i} className="rounded-xl border border-dashed border-[color:var(--sb-saffron,#F97316)]/40 bg-gradient-to-br from-[color:var(--sb-saffron,#F97316)]/5 to-fuchsia-500/5 px-4 py-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-[color:var(--sg-fg)]">{it.title}</div>
              {it.subtitle && <div className="mt-0.5 text-xs text-[color:var(--sg-muted)] leading-relaxed">{it.subtitle}</div>}
            </div>
            <Button {...linkProps} className="shrink-0 h-9 px-3 rounded-full bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white text-xs font-semibold flex items-center gap-1">
              Open <ArrowRight className="h-3.5 w-3.5"/>
            </Button>
          </div>
        );
      })}
    </div>
  );
}
